<script lang="ts">
    import { onMount } from 'svelte';
    import MarqueeTextControls from './MarqueeTextControls.svelte';
    import MarqueeTextElement from './MarqueeTextElement.svelte';
    import ResizeDetector from '@amp/web-app-components/src/components/helpers/ResizeDetector.svelte';
    import type { LCDTextFragment } from '@amp/web-app-components/src/components/LCDMetadata/types';
    import { debounce } from '@amp/web-app-components/src/utils/debounce';
    import type { MarqueeStyle } from './types';

    // Values used in calculation determining whether or not the text should animate.
    // Default to 10 to match default --lcd-line-padding
    // Primary value = context menu width + favorite button + marquee line padding
    // used w/ primary marquee line to prevent overlap w/ action buttons
    const DEFAULT_MARQUEE_ANIMATION_OFFSET = 10;
    const PRIMARY_MARQUEE_ANIMATION_OFFSET = 50;
    const MARQUEE_ANIMATION_DELAY = 3000;

    // The amount of space between the text chunk and its copy
    const MARQUEE_LINE_PADDING = 8;

    // Max text width used to prevent overlap with action buttons
    // on primary marquee line
    const MAX_PRIMARY_MARQUEE_TEXT_WIDTH = 305;

    // Amount of time to animate for a short click
    const SHORT_CLICK_ANIMATION_DURATION = 500;

    // Make wheel animation slightly longer than short click animation
    // to continue debouncing wheel events until the animation is done
    const MARQUEE_WHEEL_ANIMATION_DURATION =
        SHORT_CLICK_ANIMATION_DURATION + 50;

    export let content: LCDTextFragment[];
    export let marqueeStyle: MarqueeStyle = null;

    /**
     * Config to use to display the contextual menu.
     */
    export let contextualMenuConfig: any | null = null;

    /**
     * Config to display marquee paddle controls.
     */
    export let enableMarqueeControls: boolean = false;

    /**
     * Config to set marquee animation to infinite
     */
    export let enableInfiniteScrolling: boolean = false;

    /**
     * Checks if Preview or Radio Badge exists on secondary marquee line
     * the Preview badge is rendered when a user is NOT signed in with a
     * Premium subscription. The Radio badge exists in the context of the Music app
     * and renders if the track is related to a radio station
     */
    export let hasSecondaryBadge: boolean = false;

    /**
     * Flag to use to swap animation iteration counts when interacting
     * with paddle buttons.  Scroll in this context will scroll infinite.
     */
    let isPaddleButtonClick: boolean = false;

    let reverseAnimation: boolean = false;
    let pauseAnimation: boolean = false;
    let isAnimating: boolean = false;

    // Various element references used in calculating measurements
    let metaLineEl: HTMLDivElement;
    let scrollerEl: HTMLDivElement;
    let textContentEl: HTMLSpanElement;

    // Flag to indicate if text should scroll or not
    let isMarqueeActive: boolean = false;

    function removeAnimating(): void {
        isAnimating = false;
        reverseAnimation = false;
    }

    function pauseAnimating(): void {
        pauseAnimation = true;
    }

    function setAnimating(): void {
        if (!scrollerEl) {
            return;
        }

        if (!isAnimating) {
            isAnimating = true;
            scrollerEl.addEventListener('animationend', removeAnimating, {
                once: true,
            });
        } else {
            pauseAnimation = false;
        }
    }

    function measureMarquee(
        delayStart: boolean = true,
        recalcWidth: boolean = true,
    ): void {
        const containerWidth = metaLineEl?.getBoundingClientRect().width ?? 0;
        const textContentWidth =
            textContentEl?.getBoundingClientRect().width ?? 0;
        const spaceAvailableOnOneSide = (containerWidth - textContentWidth) / 2;
        const animationOffset =
            marqueeStyle === 'primary' &&
            textContentWidth > MAX_PRIMARY_MARQUEE_TEXT_WIDTH
                ? PRIMARY_MARQUEE_ANIMATION_OFFSET
                : DEFAULT_MARQUEE_ANIMATION_OFFSET;
        const enableMarquee = spaceAvailableOnOneSide < animationOffset;
        isMarqueeActive = enableMarquee;

        if (isMarqueeActive) {
            if (recalcWidth) {
                scrollerEl?.style.setProperty(
                    '--marquee-scroll-width',
                    `${textContentWidth + MARQUEE_LINE_PADDING}`,
                );
            }

            if (delayStart) {
                setTimeout(() => setAnimating(), MARQUEE_ANIMATION_DELAY);
            } else {
                setAnimating();
            }
        }
    }

    function resizeHandler(): void {
        // Calculate marquee measurements when viewport is resized
        measureMarquee();
    }

    function handleOnClick(e: CustomEvent): void {
        const { direction } = e.detail;

        if (e.type === 'paddleClick' || e.type === 'wheelScroll') {
            isPaddleButtonClick = true;
            reverseAnimation = direction === 'reverse';
            setAnimating();

            setTimeout(() => {
                pauseAnimating();
            }, SHORT_CLICK_ANIMATION_DURATION);
        }
    }

    // When mouse leaves the paddle button element, ensure
    // we set reset our flag so the animation iterations
    // go back to the original value
    function handleMouseLeavePaddleButton(): void {
        isPaddleButtonClick = false;
        reverseAnimation = false;
    }

    // When mouse leaves the marquee line, reset all flags
    // and restart the animation.
    function handleMouseLeaveMarqueeLine() {
        isPaddleButtonClick = false;
        reverseAnimation = false;
        setAnimating();
    }

    // Handle mouse wheel scrolls to handle both vertical and horizontal scroll wheels
    // Trackpads on laptops when using gestures will emit a wheel event despite the
    // event originating from a touch device, which emits deltas of 1 and -1.
    function handleWheelEvent(e: WheelEvent): void {
        let direction;

        if (e.deltaY > 0 || e.deltaX < 0) {
            direction = 'normal';
        } else if (e.deltaY < 0 || e.deltaX > 0) {
            direction = 'reverse';
        }

        if (direction) {
            handleOnClick(
                new CustomEvent('wheelScroll', { detail: { direction } }),
            );
        }
    }

    $: {
        if (!!content) {
            // Calculate marquee measurements whenever content changes
            // Called when tracks change and content array is updated.
            measureMarquee();
        }
    }

    onMount(() => {
        // Calculate marquee measurements on initial render
        // to ensure scrollable content animates properly
        measureMarquee();
    });
</script>

<ResizeDetector on:resizeUpdate={resizeHandler} />

<div
    class={`marquee marquee--${marqueeStyle}`}
    class:marquee--badge-mask={marqueeStyle === 'secondary' &&
        hasSecondaryBadge}
    style:--marquee-line-padding="{MARQUEE_LINE_PADDING}px"
    data-testid="marquee-text-container"
>
    <div class="marquee-line-container" bind:this={metaLineEl}>
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
            class="marquee-line"
            class:active={isMarqueeActive}
            class:inactive={!isMarqueeActive}
            class:is-animating={isAnimating}
            class:is-animating--reverse={reverseAnimation}
            class:is-paused={pauseAnimation}
            class:is-infinite={enableInfiniteScrolling}
            class:is-paddle-button-click={isPaddleButtonClick}
            data-testid="marquee-line"
            on:mouseenter={() => isMarqueeActive && pauseAnimating()}
            on:mouseleave={handleMouseLeaveMarqueeLine}
            on:wheel={debounce(
                handleWheelEvent,
                MARQUEE_WHEEL_ANIMATION_DURATION,
                true,
            )}
        >
            <div class="marquee-line__string-container">
                <div class="marquee-line__mask" dir="auto">
                    <div
                        class="marquee-line__scroller"
                        bind:this={scrollerEl}
                        data-testid="marquee-line-scroller"
                    >
                        <div class="marquee-line__scrolling-text-chunk">
                            <span
                                class="marquee-line__text-content"
                                bind:this={textContentEl}
                            >
                                {#each content as contentItem}
                                    <MarqueeTextElement
                                        {contentItem}
                                        on:navigate
                                    />
                                {/each}
                            </span>
                        </div>
                        <div
                            class="marquee-line__scrolling-text-chunk marquee-line__scrolling-text-chunk--copy"
                        >
                            <span class="marquee-line__text-content">
                                {#each content as contentItem}
                                    <MarqueeTextElement
                                        {contentItem}
                                        isCopy={true}
                                        on:navigate
                                    />
                                {/each}
                            </span>
                        </div>
                    </div>
                </div>
                {#if enableMarqueeControls && isMarqueeActive}
                    <MarqueeTextControls
                        on:mouseLeavePaddle={handleMouseLeavePaddleButton}
                        on:paddleClick={debounce(
                            handleOnClick,
                            SHORT_CLICK_ANIMATION_DURATION,
                            true,
                        )}
                    />
                {/if}

                <div class="marquee__menu-slot-container">
                    {#if $$slots['context-menu'] && !contextualMenuConfig}
                        <slot name="context-menu" />
                    {:else if contextualMenuConfig}
                        <amp-contextual-menu-button
                            config={contextualMenuConfig}
                        >
                            <div slot="trigger-content">
                                <amp-icon
                                    class="marquee__icon marquee__icon--menu"
                                    name="more-lcd"
                                />
                            </div>
                        </amp-contextual-menu-button>
                    {/if}
                </div>
            </div>
        </div>
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/helpers' as *;
    @use 'ac-sasskit/core/locale' as *;
    @use 'amp/stylekit/core/mixins/overflow-bleed' as *;
    @use '@amp/web-shared-styles/app/core/mixins/marquee' as *;
    @use '@amp/web-shared-styles/app/core/mixins/focus' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    // Vars:
    // --stopped-marquee-mask
    // --stopped-marquee-mask-rtl
    // --animated-marquee-mask
    // --hover-marquee-mask
    // --primary-paddle-controls-mask-hover
    // --marquee-line-padding

    :root {
        // Repeat identical Marquee animations for restarting animation from different states and events.
        // See: https://css-tricks.com/restart-css-animation/
        @include marquee;
        @include marquee(-playing);
        @include marquee(-restarted);
        @include marquee(-hover);
    }

    .marquee {
        width: 100%;
    }

    .marquee--primary,
    .marquee--secondary {
        &:hover .active {
            --paddle-controls-opacity: 1;
        }

        @media (--range-sidebar-hidden-down) {
            .marquee-line.inactive,
            .marquee-line__string-container {
                --lcd-line-padding: 0;
            }
        }
    }

    .marquee--primary {
        // Shift right paddle control to the left of the lcd context menu
        // and favorite buttons to prevent overlap
        --paddle-controls-offset-inline-end: #{$lcd-button-spacing * 2};
        color: var(--lcd-primary-text-color, var(--systemPrimary));
        font: var(--callout-emphasized);
    }

    // while marquee scrolling is enabled, add fade mask to primary
    // marquee line on hover or when paddle controls have focus
    .marquee--primary:hover
        .active
        .marquee-line__mask:has(+ .paddle-controls-container),
    .marquee--primary
        .active
        .marquee-line__mask:has(+ .paddle-controls-container:focus-within) {
        --primary-paddle-controls-mask-hover: #{$primary-paddle-controls-mask};

        &:dir(rtl) {
            --primary-paddle-controls-mask-hover: #{$primary-paddle-controls-mask-rtl};
        }
    }

    .marquee--secondary {
        width: var(--lcd-secondary-width, 100%);
        min-height: 13px;
        position: relative;
        color: var(--lcd-secondary-text-color, var(--systemSecondary));
        font: var(--callout);
        transition: color 0.1s ease-in;

        // Add height on secondary line to prevent focus ring clipping
        .marquee-line-container {
            width: 100%;
            height: 21px;
            position: absolute;
            top: -5px;
        }

        // while marquee scrolling is enabled, allow secondary marquee line
        // to take up the full width of its container on hover
        // or when paddle controls have focus
        &:hover:has(.active),
        &:has(.paddle-controls-container:focus-within) {
            --lcd-secondary-width: 100%;
        }
    }

    // At the xsmall breakpoint add fade mask to secondary
    // marquee line on hover or when paddle controls have focus
    // while marquee scrolling is enabled,
    .marquee--secondary:hover
        .active
        .marquee-line__mask:has(+ .paddle-controls-container),
    .marquee--secondary
        .active
        .marquee-line__mask:has(+ .paddle-controls-container:focus-within) {
        --secondary-paddle-controls-mask-hover: #{$secondary-paddle-controls-mask};

        &:dir(rtl) {
            --secondary-paddle-controls-mask-hover: #{$secondary-paddle-controls-mask-rtl};
        }
    }

    .marquee-line {
        width: 100%;
        display: flex;
        box-sizing: border-box;
        flex-direction: row;
        align-items: center;
        justify-content: var(--lcd-justify-text, center);
        overflow: clip;
        position: relative;
        text-align: var(--lcd-justify-text, center);
        text-overflow: none;
        white-space: nowrap;

        &.active {
            justify-content: flex-end;
        }

        @media (--range-xsmall-down) {
            &.inactive {
                padding: $lcd-line-padding;
            }
        }
    }

    .marquee-line__text-content {
        display: flex;
        align-items: center;
        gap: 0.333em;
    }

    .marquee-line__string-container {
        width: auto;
        min-width: 0;

        .active & {
            padding: $lcd-line-padding;
        }

        // Set position: relative when not in marquee
        // so context menu aligns relative to this element.
        .inactive & {
            position: relative;
            padding-inline-end: 0;
        }
    }

    .marquee-line__scrolling-text-chunk {
        .active & {
            padding-inline-end: var(--marquee-line-padding);
        }
    }

    .marquee-line__scrolling-text-chunk--copy {
        opacity: 0;

        .inactive & {
            display: none;
        }
    }

    .active.is-animating .marquee-line__scrolling-text-chunk--copy {
        opacity: 1;
    }

    .active {
        padding-inline-end: $marquee-inline-spacing;
        flex-grow: 1;
        flex-shrink: 1;
    }

    .marquee__menu-slot-container {
        max-height: 16px;
        position: absolute;
        top: 0;

        .active & {
            // Shift menu position to left side of favorites button
            inset-inline-end: var(--menu-position-shift, $lcd-button-spacing);
        }

        .inactive & {
            // Shift menu position to right side of text
            inset-inline-start: calc(100% + $marquee-inline-spacing);
        }
    }

    //
    // Masks
    //

    // masks are defined in chrome-player.scss because
    // they rely on the hover state at that parent level
    .marquee-line__mask {
        width: 100%;
        height: 16px;

        &:dir(rtl) {
            direction: rtl;
        }

        // Add height on secondary line to prevent focus ring clipping
        .marquee--secondary & {
            padding: 3px 0 2px;
            height: 21px;
            box-sizing: border-box;
        }
    }

    // default (primary line) mask when not animating
    .active:not(.is-animating) .marquee-line__mask {
        mask: var(
            --primary-paddle-controls-mask-hover,
            var(--stopped-marquee-mask, #{$stopped-marquee-mask})
        );

        &:dir(rtl) {
            mask: var(
                --primary-paddle-controls-mask-hover,
                var(--stopped-marquee-mask-rtl, #{$stopped-marquee-mask-rtl})
            );
        }
    }

    // default (primary line) mask when animating
    .active.is-animating .marquee-line__mask {
        mask: var(
            --primary-paddle-controls-mask-hover,
            var(--animated-marquee-mask, #{$animated-marquee-mask})
        );

        @media (--range-xsmall-down) {
            mask: var(
                --primary-paddle-controls-mask-hover,
                var(
                    --animated-marquee-mask-small,
                    #{$animated-marquee-mask-small}
                )
            );
        }
    }

    // make space for time track on secondary line for hover,
    // not animated:
    .marquee--secondary .active:not(.is-animating) .marquee-line__mask {
        mask: var(
            --secondary-mask-hover,
            var(--stopped-marquee-mask, #{$stopped-marquee-mask})
        );

        @media (--range-xsmall-down) {
            mask: var(
                --secondary-paddle-controls-mask-hover,
                var(
                    --stopped-marquee-mask-small,
                    #{$stopped-marquee-mask-small}
                )
            );
        }
    }

    // make space for time track on secondary line for hover,
    // not animated RTL
    .marquee--secondary
        .active:not(.is-animating)
        .marquee-line__mask:dir(rtl) {
        mask: var(
            --secondary-mask-hover,
            var(--stopped-marquee-mask-rtl, #{$stopped-marquee-mask-rtl})
        );

        @media (--range-xsmall-down) {
            mask: var(
                --secondary-paddle-controls-mask-hover,
                var(
                    --stopped-marquee-mask-small-rtl,
                    #{$stopped-marquee-mask-small-rtl}
                )
            );
        }
    }

    .marquee--secondary:not(.marquee--badge-mask)
        .active.is-animating
        .marquee-line__mask {
        mask: var(
            --secondary-mask-hover,
            var(--animated-marquee-mask-small, #{$animated-marquee-mask-small})
        );

        @media (--range-xsmall-down) {
            mask: var(
                --secondary-paddle-controls-mask-hover,
                var(
                    --animated-marquee-mask-small,
                    #{$animated-marquee-mask-small}
                )
            );
        }
    }

    // Apply alternate mask if Preview or Radio badge is present,
    // while marquee is stopped
    .marquee--secondary.marquee--badge-mask
        .active:not(.is-animating)
        .marquee-line__mask {
        mask: $stopped-marquee-mask-badge;

        &:dir(rtl) {
            mask: $stopped-marquee-mask-badge-rtl;
        }
    }

    // Apply alternate mask if Preview or Radio badge is present,
    // while marquee is animating
    .marquee--secondary.marquee--badge-mask
        .active.is-animating
        .marquee-line__mask {
        mask: $animated-marquee-mask-badge;
    }

    //
    // Scrolling animations
    //
    .marquee-line__scroller {
        // To create the marquee effect, we duplicate text and place them side-by-side for scrolling
        // Once the first sibling is scrolled out of view, we can reset the animation to give the
        // illusion of a continuous loop.
        display: grid;
        height: 15px;
        grid-template-columns: auto 1fr;
        z-index: var(--z-default);

        // Begin animation with `is-animating` state
        .active.is-animating & {
            animation-name: marquee; /* stylelint-disable-line */
            animation-duration: $lcd-marquee-animation-duration;
            animation-timing-function: linear;
            animation-delay: $lcd-marquee-animation-delay;
            animation-iteration-count: 1;
            animation-fill-mode: forwards;
            animation-play-state: running;
            will-change: transform; // Improve performance of animation by informing browser marquee will animate.
        }

        .active.is-paddle-button-click & {
            animation-duration: $lcd-marquee-animation-duration-short;
            animation-iteration-count: infinite;
        }

        .active.is-infinite & {
            animation-iteration-count: infinite;
        }

        .active.is-animating--reverse & {
            animation-direction: reverse;
        }

        .active.is-paused & {
            animation-play-state: paused;
        }

        // Apply RTL scrolling animation
        .active.is-animating &:dir(rtl) {
            animation-name: marquee-rtl; /* stylelint-disable-line */
        }
    }
</style>
